import {
  Component,
  OnInit,
  Injector,
  Renderer2,
  OnDestroy,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { filter, take, switchMap, takeUntil, tap } from 'rxjs/operators';

import { PathElement, NodeEntry, Node } from '@alfresco/js-api';
import { TranslationService } from '@alfresco/adf-core';

import { ContentService } from '../../components/shared/services/content.service';
import { WKInsertLinkComponent } from '../wk-insert-link/wk-insert-link.component';
import { currentSiteSelector } from '../../store/selectors/app.custom-selectors';
import { WkTreeViewService } from '../../services/wk-tree-view.service';
import { InsertMediaService } from '../../services/insert-media.service';
import { WkInsertDocumentLinkService } from '../../services/wk-link-document.service';
import { InsertLinkEvent } from '../../model/event.model';
import { AppStore } from '../../store/states/app.state';
import { cloneNode } from '../../utils/node.utils';
import { CommonConstants, LocalStorage } from '../../shared';
import { WkAppConfigService } from '../../services/wk-app-config.service';
import { onLoadingReadonlyContent } from '../../modules/wk-document-decorator';

@Component({
  selector: 'wk-link-document',
  templateUrl: './wk-link-document.component.html',
  styleUrls: ['./wk-link-document.component.scss'],
  standalone: false,
})
export class WkLinkDocumentComponent implements OnInit, OnDestroy {
  parentFolderId;
  currentFileId;
  searchDocument = '';
  dialogRef: MatDialogRef<WKInsertLinkComponent>;
  currentSite = '';
  onDestroy$ = new Subject<void>();
  node: any;
  searchType = 'document';
  mediaType = 'application|text';
  forbiddenType = 'application/x-';
  isLoading = false;
  onSearch$ = new Subject<void>();
  resultFiles = [];
  spinner;
  searchFiles;
  selectedDocumentNodes = [];
  insertLinkEvent: InsertLinkEvent;
  private lastSelectedDocument;
  private readonly treeViewService: WkTreeViewService;
  private linkText: string;
  selection = null;
  breadCrumbNode: Node;
  constructor(
    private readonly contentService: ContentService,
    private readonly nodeService: InsertMediaService,
    private readonly store: Store<AppStore>,
    private readonly injector: Injector,
    private readonly renderer: Renderer2,
    private readonly insertDocumentService: WkInsertDocumentLinkService,
    private readonly translationService: TranslationService,
    private readonly appConfigService: WkAppConfigService
  ) {
    this.treeViewService =
      this.injector.get<WkTreeViewService>(WkTreeViewService);
  }

  ngOnInit() {
    this.linkText = '';
    this.isLoading = true;
    this.getDocuments();
    if (this.insertLinkEvent.linkAttributes) {
      this.linkText = this.insertLinkEvent.linkAttributes.linkText;
    } else if (this.insertLinkEvent.editor) {
      const selection = this.insertLinkEvent.editor.model.document.selection;
      this.selection = selection;
      const ranges = selection.getRanges();

      this.checkRanges(ranges);
    }
    this.nodeService
      .getClickedFolderDetails()
      .pipe(
        switchMap((result: NodeEntry) => {
          if (result) {
            return this.contentService.getNode(result.entry.id);
          }
          return of(undefined);
        }),
        takeUntil(this.onDestroy$)
      )
      .subscribe((node: NodeEntry) => {
        if (node) {
          this.breadCrumbNode = cloneNode(node).entry;
        }
      });
  }

  closeDialog(): void {
    this.dialogRef.close();
    this.insertLinkEvent.editor?.focus();
    localStorage.removeItem(LocalStorage.EDIT_LINK);
  }

  ngOnDestroy() {
    this.nodeService.setClickedFolderDetails('');
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private checkRanges(ranges: any) {
    for (const range of ranges) {
      for (const item of range?.getItems()) {
        if (typeof item.data === 'string' && item.data.length) {
          this.linkText += item.data;
        }
      }
    }
  }

  search() {
    if (this.searchDocument) {
      this.nodeService.selectedNode.clear();
      this.store
        .select(currentSiteSelector)
        .pipe(
          filter((currentSite) => currentSite && currentSite !== 'no-site'),
          take(1),
          switchMap(() =>
            this.treeViewService.getNodeChildren(null, this.searchDocument)
          ),
          takeUntil(this.onSearch$)
        )
        .subscribe((data) => {
          this.resultFiles = data?.list?.entries;
          this.spinner = false;
        });
      this.searchFiles = true;
      this.spinner = true;
    } else {
      this.getDocuments();
      this.searchFiles = false;
    }
  }

  onDocumentSelected(event, node) {
    const selectedChild = 'selected-child';
    const hasClass = event.target
      .closest('.child')
      .classList?.contains(selectedChild);

    if (this.lastSelectedDocument) {
      this.renderer.removeClass(
        this.lastSelectedDocument.target.closest('.child'),
        selectedChild
      );
    }
    if (!hasClass) {
      this.renderer.addClass(event.target.closest('.child'), selectedChild);
      this.selectedDocumentNodes = [node];
    } else {
      this.selectedDocumentNodes = [];
    }
    this.lastSelectedDocument = event;
    const cloneData = JSON.parse(JSON.stringify(this.selectedDocumentNodes));
    for (const element of cloneData) {
      this.nodeService.selectedNode.set(element.id, element);
    }
  }

  addLinkSelected() {
    this.store
      .select(currentSiteSelector)
      .pipe(
        filter(
          (currentSite) => currentSite !== '' && currentSite !== 'no-site'
        ),
        take(1),
        tap((currentSite) => {
          const currentSiteConfig = this.getCurrentSiteConfig(currentSite);
          const { type, subtypes } = currentSiteConfig?.documentNodeType || {};
          const { entry } = this.nodeService.selectedNode.entries().next()
            .value[1];
          const { properties, nodeType } = entry;

          this.insertDocumentService.currentEventLinkDocument.next({
            evt: this.insertLinkEvent,
          });
          this.contentService.setSplitModeFile({
            isSplitMode: true,
            nodeSelected: entry,
            selection: this.selection,
          });
          if (this.linkText === '') {
            this.linkText = this.translationService.instant(
              `ADF_INSERT_LINK.DEFAULT_TEXT`
            );
          }
          const selectedContent = this.insertLinkEvent.editor.data.stringify(
            this.insertLinkEvent.editor.model.getSelectedContent(
              this.insertLinkEvent.editor.model.document.selection
            )
          );
          this.insertDocumentService.setLinkDetails({
            resource: properties['apollo:tenantId'],
            linkText: this.linkText,
            linkElement: this.insertLinkEvent?.linkAttributes?.linkElement,
            typeOf:
              nodeType === type || subtypes?.includes(nodeType)
                ? CommonConstants.pciCoreBibliographicResource
                : CommonConstants.pciCoreMediaResource,
            textSelected: selectedContent !== '',
          });
          this.dialogRef.close();
          this.store.dispatch(
            onLoadingReadonlyContent({
              id: entry.id,
              url: '',
            })
          );
        })
      )
      .subscribe();
  }

  // This function takes a currentSite string as a parameter and returns the current site's configuration
  private getCurrentSiteConfig(currentSite: string): any {
    const sitesConfigs = this.appConfigService.get('sites');
    const sitesConfigsArray = Object.values(sitesConfigs);
    return sitesConfigsArray.find(
      (siteConfig) => siteConfig.id === currentSite
    );
  }
  async getDocuments() {
    this.node = await this.nodeService.getNodeElements();
    if (this.node.path) {
      const breadNode = { entry: this.node };
      this.breadCrumbNode = cloneNode(breadNode).entry;
    }
    this.isLoading = false;
  }

  isDisabled(): boolean {
    return !(
      this.nodeService.selectedNode && this.nodeService.selectedNode.size > 0
    );
  }

  async onBreadcrumbNavigate(route: PathElement) {
    const node = await this.nodeService.getBreadCrumb(route);
    this.updateNode(node);
  }

  updateNode(node: NodeEntry) {
    this.node = node.entry;
    this.breadCrumbNode = cloneNode(node).entry;
  }
}
